import { giphyDarkGrey, giphyLightestGrey } from '@giphy/colors'
import { useContext } from 'react'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM, DUPLICATE_UPLOAD_ID_EXTENSION } from '../../../constants'
import { CreationToolsContext } from '../../../manager'
import CreationToolGradient from '../../../types/gradient'
import CreationToolOutputType from '../../../types/output-type'
import CreationToolSourceEditor from '../../../types/source-editor'
import CreationToolStatus from '../../../types/status'
import getStickerFromSource from '../../../utils/get-sticker-from-source'
import { MenuAddIcon, TrimEditorIcon } from '../../icons'
import { useAvailableEditors } from '../hooks/use-available-editors'
import { useDuplicatableTypes } from '../hooks/use-duplicatable-types'
import { CreationToolUploadContext } from '../manager'
import { CreationToolUploadSource } from '../types'
import Item from './dropdown-item'

const Dropdown = styled.div`
    background: #000;
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
    box-sizing: border-box;
    color: ${giphyLightestGrey};
    font-size: 15px;
    left: 50%;
    margin-left: -110px;
    padding: 20px;
    position: absolute;
    top: 100%;
    width: 220px;

    &:before {
        border-bottom: 6px solid #000;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        content: '';
        height: 0;
        left: 50%;
        margin-left: -6px;
        position: absolute;
        top: -6px;
        width: 0;
    }
`

const Items = styled.div`
    svg {
        transition: opacity 0.15s ease-out;
    }

    &:hover svg {
        opacity: 0.5;
    }

    &:hover > div:hover svg {
        opacity: 1;
    }
`

const Divider = styled.div`
    background: ${giphyDarkGrey};
    border-radius: 1px;
    height: 2px;
    margin: 6px 0;
`

type Props = {
    className?: string
    source: CreationToolUploadSource
    setIsPreviewing?: (isPreviewing: boolean) => void
    onEditorClick: (editor?: CreationToolSourceEditor) => void
}

const CreationUploadItemEditMenuDropdown = ({ className, source, setIsPreviewing, onEditorClick }: Props) => {
    const { tool } = useContext(CreationToolsContext)
    const { uploadSources, addUploadSource, updateUploadSource } = useContext(CreationToolUploadContext)

    const editGradient = CreationToolSourceEditor.gradient(CreationToolSourceEditor.Edit)
    const editColors = CreationToolGradient.colors(editGradient)
    const availableEditors = useAvailableEditors(source, tool)
    const duplicatableTypes = useDuplicatableTypes(source, uploadSources)

    const onDuplicateClick = async (type: CreationToolOutputType) => {
        if (!type) return

        const isConvertToSticker = type === CreationToolOutputType.Sticker
        const newSource = {
            ...source,
            id: `${source.id}${DUPLICATE_UPLOAD_ID_EXTENSION}`,
            status: isConvertToSticker ? CreationToolStatus.ConvertingToSticker : CreationToolStatus.Idle,
            outputType: type,
            isDuplicate: true,
        }

        setIsPreviewing && setIsPreviewing(false)
        addUploadSource(newSource)

        if (isConvertToSticker) {
            const sticker = await getStickerFromSource(newSource.source)
            updateUploadSource({
                ...newSource,
                edits: undefined,
                source: sticker,
                status: CreationToolStatus.Idle,
            })
        }
    }

    return (
        <Dropdown className={className}>
            <Items>
                <>
                    {availableEditors.map((editor) => {
                        const title = CreationToolSourceEditor.menuTitle(editor)
                        const gradient = CreationToolSourceEditor.gradient(editor)
                        const colors = CreationToolGradient.colors(gradient)
                        const Icon = CreationToolSourceEditor.icon(editor)
                        return (
                            <Item key={editor} label={title} onClick={() => onEditorClick(editor)}>
                                <Icon colors={colors} />
                            </Item>
                        )
                    })}
                    <Item label="Trim" onClick={() => onEditorClick()}>
                        <TrimEditorIcon colors={editColors} />
                    </Item>
                </>
                {duplicatableTypes.length > 0 && (
                    <>
                        <Divider />
                        {duplicatableTypes.map((type) => {
                            const gradient = CreationToolOutputType.gradient(type)
                            const colors = CreationToolGradient.colors(gradient)

                            return (
                                <Item
                                    key={type}
                                    label={`Duplicate as ${CreationToolOutputType.title(type)}`}
                                    onClick={() => onDuplicateClick(type)}
                                >
                                    <MenuAddIcon colors={colors} />
                                </Item>
                            )
                        })}
                    </>
                )}
            </Items>
        </Dropdown>
    )
}

export default CreationUploadItemEditMenuDropdown
